import { Store } from '@betgames/bg-state-manager';
import { IHowToPlayModel } from '@bg-shared/business/HowToPlay';
import { GamificationAssetStatus } from '@bg-shared/business/Gamification/enums';

import {
    IAssetsPurchaseHistory,
    IGamificationAchievedBadge,
    IGamificationAsset,
    IGamificationBadge,
    IGamificationInbox,
    IGamificationModel,
    IGamificationSettings,
    IGamificationTournament,
    INotificationMessage,
    IGamificationAssetEvent,
} from '../interfaces';

export default class GamificationStore extends Store<IGamificationModel> {
    constructor() {
        super({
            key: 'Gamification',
            default: {
                settings: {
                    appKey: null,
                    sdkUrl: null,
                    settingsLoaded: false,
                    envPrefix: null,
                    clientToken: null,
                    loaded: false,
                    error: false,
                },
                badges: {
                    achieved: [],
                    challenges: [],
                },
                inboxes: [],
                profile: {
                    userInfo: {
                        id: null,
                        name: null,
                        signedUser: null,
                        image: null,
                        points: 0,
                        coins: 0,
                    },
                    level: {
                        points: 0,
                        number: 0,
                        previousLevel: 0,
                        nextLevelPoints: 0,
                    },
                    blocked: false,
                },
                notifications: {
                    shownNotifications: [],
                    notificationMessage: null,
                },
                tournaments: {
                    list: [],
                    optInIds: [],
                },
                shop: {
                    items: [],
                    purchasesByHashes: {},
                },
                introPageTranslations: null,
            },
        });
    }

    public setInitialData(
        data: Pick<
            IGamificationModel,
            'badges' | 'inboxes' | 'profile' | 'notifications' | 'tournaments'
        >,
    ): void {
        this.update((draft) => {
            draft.badges.challenges = data.badges.challenges;
            draft.badges.achieved = data.badges.achieved;
            draft.inboxes = data.inboxes;
            draft.profile.userInfo = data.profile.userInfo;
            draft.notifications.shownNotifications = data.notifications.shownNotifications;
            draft.profile.level = data.profile.level;
            draft.tournaments = data.tournaments;
            draft.settings.loaded = true;
        });
    }

    public setSettingsLoaded(): void {
        this.update((draft) => {
            draft.settings.settingsLoaded = true;
        });
    }

    public updateChallenges(challenges: IGamificationBadge[]): void {
        this.update((draft) => {
            draft.badges.challenges = challenges;
        });
    }

    public updateTournaments(tournaments: IGamificationTournament[]): void {
        this.update((draft) => {
            draft.tournaments.list = tournaments;
        });
    }

    public closeAllInboxes(): void {
        this.update((draft) => {
            draft.inboxes = draft.inboxes.map((inbox) => ({
                ...inbox,
                expanded: false,
            }));
        });
    }

    public updateSettings(settings: IGamificationSettings): void {
        this.update((draft) => {
            draft.settings.envPrefix = settings.envPrefix;
            draft.settings.appKey = settings.appKey;
            draft.settings.sdkUrl = settings.sdkUrl;
            draft.settings.clientToken = settings.clientToken;
            draft.profile.blocked = settings.profile?.blocked;
        });
    }

    public markInboxSeen(id: string): void {
        this.update((draft) => {
            const inbox = draft.inboxes.find((inboxItem) => inboxItem.id === id);

            if (inbox) {
                inbox.unseen = false;
            }
        });
    }

    public updateInboxes(inboxes: IGamificationInbox[]): void {
        this.update((draft) => {
            draft.inboxes = inboxes;
        });
    }

    public toggleInboxExpand(inbox: IGamificationInbox): void {
        this.update((draft) => {
            draft.inboxes = draft.inboxes.map((currentInbox) => {
                if (currentInbox.id === inbox.id) {
                    return {
                        ...inbox,
                        expanded: !inbox.expanded,
                    };
                }

                return currentInbox;
            });
        });
    }

    public markBadgeSeen(id: string): void {
        this.update((draft) => {
            const achievedBadge = draft.badges.achieved.find((badge) => badge.id === id);

            if (achievedBadge) {
                achievedBadge.unseen = false;
            }
        });
    }

    public updateShownNotifications(notificationsIds: string[]): void {
        this.update((draft) => {
            draft.notifications.shownNotifications = notificationsIds;
        });
    }

    public toggleProfileBlock(flag: boolean): void {
        this.update((draft) => {
            draft.profile.blocked = flag;
        });
    }

    public markAchievedBadgesSeen(): void {
        this.update((draft) => {
            const unseenAchievedBadges = draft.badges.achieved.filter((badge) => badge.unread);

            unseenAchievedBadges.forEach((badge) => {
                badge.unseen = false;
                badge.unread = false;
            });
        });
    }

    public markChallengesBadgesSeen(): void {
        this.update((draft) => {
            const unseenChallengesBadges = draft.badges.challenges.filter((badge) => badge.unread);

            unseenChallengesBadges.forEach((badge) => {
                badge.unread = false;
            });
        });
    }

    public updateActivities(
        activities: [IGamificationBadge[], IGamificationAchievedBadge[]],
    ): void {
        const [updatedChallenges, achievedChallenges] = activities;

        this.update((draft) => {
            draft.badges.challenges = updatedChallenges;
            draft.badges.achieved.unshift(...achievedChallenges);
        });
    }

    public updateUserImage(image: string): void {
        this.update((draft) => {
            draft.profile.userInfo.image = image;
        });
    }

    public updateUserCurrencies(points: number, coins: number): void {
        this.update((draft) => {
            draft.profile.userInfo.points = points;
            draft.profile.userInfo.coins = coins;
        });
    }

    public updateLevel(number: number, points: number): void {
        this.update((draft) => {
            if (draft.profile.level.number !== number) {
                draft.profile.level.previousLevel = draft.profile.level.number;
                draft.profile.level.number = number;
                draft.profile.level.points = points;
            }
        });
    }

    public updateNextLevelPoints(points: number): void {
        this.update((draft) => {
            draft.profile.level.nextLevelPoints = points;
        });
    }

    public setNotificationMessage(message: INotificationMessage): void {
        this.update((draft) => {
            draft.notifications.notificationMessage = message;
        });
    }

    public clearStore(): void {
        this.reset();
        this.update((draft) => {
            draft.settings.settingsLoaded = true;
        });
    }

    public toggleTournamentParticipation(tournamentId: string, flag: boolean): void {
        this.update((draft) => {
            if (flag) {
                draft.tournaments.optInIds.push(tournamentId);
            } else {
                draft.tournaments.optInIds = draft.tournaments.optInIds.filter(
                    (id) => id !== tournamentId,
                );
            }
        });
    }

    public updateShopAssets(items: IGamificationAsset[], history: IAssetsPurchaseHistory): void {
        this.update((draft) => {
            draft.shop.items = items;
            draft.shop.purchasesByHashes = history;
        });
    }

    public updateAssetsHistory(history: IAssetsPurchaseHistory): void {
        this.update((draft) => {
            draft.shop.purchasesByHashes = history;
        });
    }

    public assignPurchasedAsset(data: IGamificationAssetEvent): void {
        this.update((draft) => {
            if (!draft.shop.purchasesByHashes[data.assetHash]) {
                return;
            }

            const existingPurchase = draft.shop.purchasesByHashes[data.assetHash].find(
                (purchase) => purchase.id === data.purchaseId,
            );

            if (existingPurchase) {
                existingPurchase.status = GamificationAssetStatus.ASSIGNED;
                existingPurchase.isUsed = data.isUsed;
                existingPurchase.usedAt = data.usedAt;
            }
        });
    }

    public updateIntroPageTranslations(items: IHowToPlayModel): void {
        this.update((draft) => {
            draft.introPageTranslations = items;
        });
    }
}

export const gamificationStore = new GamificationStore();
